import React, { useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Form, ListGroup, Badge } from 'react-bootstrap';

// Example 1: Simple Counter State
const CounterExample = () => {
  const [count, setCount] = useState(0);

  const increment = () => {
    setCount((prevCount) => {
      console.log('📈 Counter: Incrementing from', prevCount, 'to', prevCount + 1);
      return prevCount + 1;
    });
  };

  const decrement = () => {
    setCount((prevCount) => {
      console.log('📉 Counter: Decrementing from', prevCount, 'to', prevCount - 1);
      return prevCount - 1;
    });
  };

  const reset = () => {
    console.log('🔄 Counter: Resetting from', count, 'to 0');
    setCount(0);
  };

  return (
    <Card className="mb-3">
      <Card.Header className="bg-primary text-white">Example 1: Counter State</Card.Header>
      <Card.Body>
        <div className="text-center">
          <h2 className="display-4">{count}</h2>
          <div className="d-flex gap-2 justify-content-center">
            <Button variant="success" onClick={increment}>
              ➕ Increment
            </Button>
            <Button variant="danger" onClick={decrement}>
              ➖ Decrement
            </Button>
            <Button variant="secondary" onClick={reset}>
              🔄 Reset
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

// Example 2: Form Input State
const FormInputExample = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  const handleNameChange = (e) => {
    const newName = e.target.value;
    console.log('✏️ Form Input: Name changed to', newName);
    setName(newName);
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    console.log('✏️ Form Input: Email changed to', newEmail);
    setEmail(newEmail);
  };

  return (
    <Card className="mb-3">
      <Card.Header className="bg-success text-white">Example 2: Form Input State</Card.Header>
      <Card.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control 
              type="text" 
              placeholder="Enter your name"
              value={name}
              onChange={handleNameChange}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control 
              type="email" 
              placeholder="Enter your email"
              value={email}
              onChange={handleEmailChange}
            />
          </Form.Group>
        </Form>
        <Alert variant="info" className="mb-0">
          <strong>Current State:</strong>
          <div>Name: {name || '(empty)'}</div>
          <div>Email: {email || '(empty)'}</div>
        </Alert>
      </Card.Body>
    </Card>
  );
};

// Example 3: Object State (Immutable Update Pattern)
const ObjectStateExample = () => {
  const [user, setUser] = useState({
    name: '',
    age: '',
    city: ''
  });

  const updateField = (field, value) => {
    setUser((prevUser) => {
      const updatedUser = { ...prevUser, [field]: value };
      console.log('👤 Object State Update:', {
        field,
        oldValue: prevUser[field],
        newValue: value,
        fullObject: updatedUser
      });
      return updatedUser;
    });
  };

  return (
    <Card className="mb-3">
      <Card.Header className="bg-warning">Example 3: Object State (Immutable Updates)</Card.Header>
      <Card.Body>
        <Form>
          <Form.Group className="mb-2">
            <Form.Label>Name</Form.Label>
            <Form.Control 
              type="text" 
              value={user.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Enter name"
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Age</Form.Label>
            <Form.Control 
              type="number" 
              value={user.age}
              onChange={(e) => updateField('age', e.target.value)}
              placeholder="Enter age"
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>City</Form.Label>
            <Form.Control 
              type="text" 
              value={user.city}
              onChange={(e) => updateField('city', e.target.value)}
              placeholder="Enter city"
            />
          </Form.Group>
        </Form>
        <Alert variant="success">
          <strong>User Object:</strong>
          <pre className="mb-0">{JSON.stringify(user, null, 2)}</pre>
        </Alert>
      </Card.Body>
    </Card>
  );
};

// Example 4: Array State (Add/Remove Items)
const ArrayStateExample = () => {
  const [items, setItems] = useState([]);
  const [inputValue, setInputValue] = useState('');

  const addItem = () => {
    if (inputValue.trim()) {
      const newItem = {
        id: Date.now(),
        name: inputValue,
        timestamp: new Date().toLocaleTimeString()
      };
      
      setItems((prevItems) => {
        const updatedItems = [...prevItems, newItem];
        console.log('➕ Array State: Adding item', newItem);
        console.log('   Previous length:', prevItems.length, '→ New length:', updatedItems.length);
        console.log('   All items:', updatedItems);
        return updatedItems;
      });
      
      setInputValue('');
    }
  };

  const removeItem = (id) => {
    setItems((prevItems) => {
      const updatedItems = prevItems.filter(item => item.id !== id);
      console.log('➖ Array State: Removing item with id', id);
      console.log('   Previous length:', prevItems.length, '→ New length:', updatedItems.length);
      return updatedItems;
    });
  };

  const clearAll = () => {
    console.log('🗑️ Array State: Clearing all items (', items.length, 'items)');
    setItems([]);
  };

  return (
    <Card className="mb-3">
      <Card.Header className="bg-danger text-white">Example 4: Array State (Add/Remove)</Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <div className="d-flex gap-2">
            <Form.Control 
              type="text" 
              placeholder="Enter item name"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addItem()}
            />
            <Button variant="primary" onClick={addItem}>
              Add Item
            </Button>
            <Button variant="outline-danger" onClick={clearAll}>
              Clear All
            </Button>
          </div>
        </Form.Group>

        <Alert variant="info">
          <strong>Total Items:</strong> {items.length}
        </Alert>

        <ListGroup>
          {items.length === 0 ? (
            <Alert variant="secondary">No items yet. Add some above!</Alert>
          ) : (
            items.map((item) => (
              <ListGroup.Item 
                key={item.id}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <strong>{item.name}</strong>
                  <Badge bg="secondary" className="ms-2">{item.timestamp}</Badge>
                </div>
                <Button 
                  variant="outline-danger" 
                  size="sm"
                  onClick={() => removeItem(item.id)}
                >
                  Remove
                </Button>
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
      </Card.Body>
    </Card>
  );
};

// Main Lesson Component
const Lesson03_State = () => {
  useEffect(() => {
    console.log('📖 Lesson 03: State Management (useState) - Loaded');
    console.log('📚 Learning Objectives:');
    console.log('   1. Understand useState hook basics');
    console.log('   2. Update state with primitive values');
    console.log('   3. Update state with objects (immutably)');
    console.log('   4. Update state with arrays (immutably)');
    console.log('   5. Use previous state pattern');
  }, []);

  return (
    <LessonLayout 
      lessonNumber={3}
      title="State Management with useState" 
      description="Master state management using the useState hook with various data types"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>useState hook fundamentals</li>
          <li>Updating state with different data types</li>
          <li>Immutable update patterns for objects and arrays</li>
          <li>Using previous state for reliable updates</li>
        </ul>
      </Alert>

      <Row>
        <Col md={6}>
          <CounterExample />
        </Col>
        <Col md={6}>
          <FormInputExample />
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <ObjectStateExample />
        </Col>
        <Col md={6}>
          <ArrayStateExample />
        </Col>
      </Row>

      <h4 className="mt-4">📊 State Update Patterns</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Data Type</th>
                <th>Update Pattern</th>
                <th>Example</th>
                <th>Important Note</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Primitive</td>
                <td>Direct update</td>
                <td><code>setCount(count + 1)</code></td>
                <td>Simple and straightforward</td>
              </tr>
              <tr>
                <td>Primitive (prev)</td>
                <td>Function update</td>
                <td><code>setCount(prev =&gt; prev + 1)</code></td>
                <td>✅ More reliable</td>
              </tr>
              <tr>
                <td>Object</td>
                <td>Spread operator</td>
                <td><code>{`setUser({ ...user, name: 'New' })`}</code></td>
                <td>⚠️ Must spread to maintain immutability</td>
              </tr>
              <tr>
                <td>Array (add)</td>
                <td>Spread operator</td>
                <td><code>[...items, newItem]</code></td>
                <td>Creates new array</td>
              </tr>
              <tr>
                <td>Array (remove)</td>
                <td>Filter method</td>
                <td><code>items.filter(i =&gt; i.id !== id)</code></td>
                <td>Returns new array</td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Best Practices:</strong>
        <ul className="mb-0">
          <li>Use functional updates when new state depends on previous state</li>
          <li>Never mutate state directly - always create new objects/arrays</li>
          <li>For objects: use spread operator <code>{`{ ...obj, key: value }`}</code></li>
          <li>For arrays: use spread <code>[...arr, item]</code> or filter/map methods</li>
          <li>State updates are asynchronous - don't rely on immediate changes</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson03_State;
